import {
  extendTheme,
  theme as chakraBaseTheme,
  type StyleFunctionProps,
  type ThemeConfig,
} from "@chakra-ui/react";

/**
 * Chakra theme for ipscore-website.
 *
 * Spec: specs/active/2026-08-29-website-design-system-adoption (Phase 1, steps 2-3)
 *
 * This file maps NAMES onto the values declared in `src/styles/tokens.css`.
 * It deliberately holds no colour literals: a value lives in exactly one place,
 * and that place is the token layer (which in turn expresses
 * docs/design/design-system.md).
 *
 * Not overridden on purpose:
 *  - `space`     — Chakra's default scale is already the 4px grid the design
 *                  system specifies, so `p={4}` already means 16px.
 *  - `fontSizes` — Chakra's default scale already matches the design system's
 *                  type scale step for step (xs 12 … 4xl 36).
 *  - `colors.gray` — left as Chakra ships it. Redefining a scale step would move
 *                  every decorative use of `gray.500` as well as the textual
 *                  ones, and would quietly mean something different from what
 *                  any Chakra reader expects. The textual call sites were moved
 *                  to `text.secondary` by hand instead (Phase 1, step 6).
 */

/** The real initial mode comes from `utils/appearance/colorModeManager` (cookie
 *  → <html data-theme> set by the pre-paint script → OS preference); "light"
 *  is only Chakra's last-resort fallback. `useSystemColorMode` stays false: it
 *  would override an explicit choice whenever the OS setting changes. */
const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

/** Raw brand ramp. Values come from the token layer, which switches with the
 *  colour mode, so these names are mode-agnostic by construction. */
const colors = {
  brand: {
    subtle: "var(--ips-primary-subtle)",
    /** Brand identity: focus rings, icons, borders, large text. 4.03:1 on white
     *  — clears the 3:1 non-text bar, FAILS the 4.5:1 normal-text bar. */
    accent: "var(--ips-primary)",
    /** AA-safe: normal-size text, and any fill sitting under a normal-size
     *  label. 5.42:1 on white. */
    solid: "var(--ips-primary-strong)",
    hover: "var(--ips-primary-hover)",
    active: "var(--ips-primary-active)",
    fg: "var(--ips-primary-fg)",
  },
};

/** Semantic roles. A component asks for a role; the token layer decides the
 *  value for the active colour mode (and, from Phase 2, the active style). */
const semanticTokens = {
  colors: {
    "bg.page": "var(--ips-bg-page)",
    /** Opaque in every combination (gate-asserted). Use for anything painted
     *  ON or UNDER a control (switch thumb, radio ground/dot); `bg.surface` is
     *  translucent in Expressive dark and is for containers only. */
    "bg.default": "var(--ips-bg)",
    "bg.surface": "var(--ips-surface)",
    "bg.surfaceHover": "var(--ips-surface-hover)",
    "bg.overlay": "var(--ips-surface-overlay)",

    /** Decorative only — 1.23:1 in light. Never use it to bound a control. */
    "border.subtle": "var(--ips-border)",
    /** Functional: inputs, toggles, anything WCAG 1.4.11 covers. */
    "border.strong": "var(--ips-border-strong)",

    "text.primary": "var(--ips-text)",
    "text.secondary": "var(--ips-text-secondary)",
    /** Contrast-exempt. Disabled and placeholder only — never meaning. */
    "text.disabled": "var(--ips-text-disabled)",
    "text.inverse": "var(--ips-text-inverse)",
    /** Decorative-hue text (AA on every ground). Not a status, not a link. */
    "text.accent": "var(--ips-text-accent)",

    "status.success": "var(--ips-success)",
    "status.successSubtle": "var(--ips-success-subtle)",
    /** Status label ON its own *Subtle ground (risk badges), and status text on
     *  a row that may take the surfaceHover ground (balance history): the base
     *  value is 4.39:1 / 4.33:1 there in light mode. Equal to the base in dark. */
    "status.successStrong": "var(--ips-success-strong)",
    "status.error": "var(--ips-error)",
    "status.errorSubtle": "var(--ips-error-subtle)",
    /** Error text on the bare page ground (form errors): AA at the worst
     *  Expressive-light page point, where status.error is not. */
    "status.errorStrong": "var(--ips-error-strong)",
    "status.warning": "var(--ips-warning)",
    "status.warningSubtle": "var(--ips-warning-subtle)",
    "status.warningStrong": "var(--ips-warning-strong)",
    "status.info": "var(--ips-info)",
    "status.infoSubtle": "var(--ips-info-subtle)",
    /** Info label ON status.infoSubtle: the base value is 4.40:1 there in dark. */
    "status.infoStrong": "var(--ips-info-strong)",

    "neutral.50": "var(--ips-neutral-50)",
    "neutral.100": "var(--ips-neutral-100)",
    "neutral.200": "var(--ips-neutral-200)",
    "neutral.400": "var(--ips-neutral-400)",
    "neutral.600": "var(--ips-neutral-600)",
    "neutral.900": "var(--ips-neutral-900)",

    /** Chart series (non-text, 3:1 bar). recharts cannot read a token name:
     *  resolve these with `useToken("colors", [...])` and pass the result. */
    "chart.1": "var(--ips-chart-1)",
    "chart.2": "var(--ips-chart-2)",
    "chart.3": "var(--ips-chart-3)",
    "chart.4": "var(--ips-chart-4)",
    /** Icon colour on a chart-series fill (MetricCard). */
    "chart.fg": "var(--ips-chart-fg)",

    /** Photo scrim and the text on it (news cards). Mode-invariant: the ground
     *  is an image. Apply the scrim at 60% opacity — see tokens.css. */
    "bg.scrim": "var(--ips-scrim)",
    "text.onScrim": "var(--ips-on-scrim)",
    /** Label on a status.success fill (support-chat header, visitor bubble). */
    "status.successFg": "var(--ips-success-fg)",
  },
};

const radii = {
  none: "var(--ips-radius-none)",
  sm: "var(--ips-radius-sm)",
  md: "var(--ips-radius-md)",
  lg: "var(--ips-radius-lg)",
  xl: "var(--ips-radius-xl)",
  full: "var(--ips-radius-full)",
};

const shadows = {
  sm: "var(--ips-shadow-sm)",
  md: "var(--ips-shadow-md)",
  lg: "var(--ips-shadow-lg)",
};

const fonts = {
  heading: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  body: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  mono: `ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace`,
};

/**
 * Component recipes.
 *
 * Every override composes over Chakra's base recipe and changes only the
 * defect it names; everything else keeps Chakra's own styling.
 *
 * 1. Solid fills under a white label (US2). Chakra renders `*.500` fills under
 *    a white label in light mode: blue 4.03:1, green 3.37:1, orange 3.74:1 and
 *    so on — below the 4.5:1 a normal-size label needs. The light-mode fill
 *    moves to the first AA-safe shade of the same hue (computed vs white:
 *    green.600 4.54, teal.600 5.03, red.600 5.47, orange.600 4.57,
 *    gray.600 7.53, yellow.700 5.54, cyan.800 5.81, pink.600 5.52). Blue maps onto the brand
 *    scale (`brand.solid`, 5.42:1). Purple.500 already passes (4.84) and is left
 *    alone. Dark mode is untouched: it already puts a dark label on `*.200`.
 *    `outline`, `ghost` and `subtle` variants already pass and are not touched.
 *
 * 2. Input / Select outlines (WCAG 1.4.11). The resting border moves from the
 *    decorative `gray.200` (1.23:1) to the functional `border.strong`; hover
 *    darkens instead of lightening; focus is the 2px brand outline with a 2px
 *    offset that ux-principles.md specifies. Select is overridden separately
 *    because Chakra builds `selectTheme` from the base input variants
 *    statically — an Input override never reaches it.
 */

/** Light-mode solid Button fills. Yellow / cyan Buttons already use a dark
 *  label in Chakra (accessibleColorMap) and gray is a light fill, so only the
 *  four white-label schemes that fail are listed. */
const SOLID_BUTTON_SHADES: Record<string, { bg: string; hover: string; active: string }> = {
  green: { bg: "green.600", hover: "green.700", active: "green.800" },
  teal: { bg: "teal.600", hover: "teal.700", active: "teal.800" },
  red: { bg: "red.600", hover: "red.700", active: "red.800" },
  orange: { bg: "orange.600", hover: "orange.700", active: "orange.800" },
};

/** Light-mode solid Badge / Tag fills (both share the badge recipe). */
const SOLID_BADGE_SHADES: Record<string, string> = {
  blue: "brand.solid",
  green: "green.600",
  teal: "teal.600",
  red: "red.600",
  orange: "orange.600",
  gray: "gray.600",
  yellow: "yellow.700",
  cyan: "cyan.800",
  purple: "purple.500", // 4.84:1 light, listed so its dark fill is fixed too
  // pink.500 is 4.27:1 under white (InvoiceStatus "canceled"); pink.600 is
  // 5.52:1. Dark: pink.200 under gray.800 is 9.86:1. No pink Button exists.
  pink: "pink.600",
};

/** The CSS variable Chakra's badge recipe paints its fill from (Tag reuses it). */
const BADGE_BG_VAR = "--badge-bg";
/** The CSS variable Chakra's badge recipe paints its label from. */
const BADGE_COLOR_VAR = "--badge-color";

const solidButton = (props: StyleFunctionProps) => {
  const base = chakraBaseTheme.components.Button.variants!.solid(props);
  if (props.colorScheme === "blue") {
    return {
      ...base,
      bg: "brand.solid",
      color: "brand.fg",
      _hover: { bg: "brand.hover", _disabled: { bg: "brand.solid" } },
      _active: { bg: "brand.active" },
    };
  }
  const shades = SOLID_BUTTON_SHADES[props.colorScheme];
  if (!shades || props.colorMode === "dark") return base;
  return {
    ...base,
    bg: shades.bg,
    _hover: { bg: shades.hover, _disabled: { bg: shades.bg } },
    _active: { bg: shades.active },
  };
};

/**
 * Light: the AA-safe fill under Chakra's white label. Dark: Chakra's own
 * `_dark` block puts whiteAlpha.800 on a 60%-transparent `*.500` (3.3:1 for
 * green, measured in a browser 2026-10-30), so it is replaced with the same
 * treatment the dark solid Button already uses: a `*.200` fill under gray.800.
 */
const solidBadgeVars = (colorScheme: string, base: Record<string, unknown>) => ({
  ...base,
  [BADGE_BG_VAR]: `colors.${SOLID_BADGE_SHADES[colorScheme]}`,
  _dark: {
    [BADGE_BG_VAR]: `colors.${colorScheme}.200`,
    [BADGE_COLOR_VAR]: "colors.gray.800",
  },
});

const solidBadge = (props: StyleFunctionProps) => {
  const base = chakraBaseTheme.components.Badge.variants!.solid(props);
  if (!SOLID_BADGE_SHADES[props.colorScheme]) return base;
  return solidBadgeVars(props.colorScheme, base);
};

const solidTag = (props: StyleFunctionProps) => {
  const base = chakraBaseTheme.components.Tag.variants!.solid(props);
  if (!SOLID_BADGE_SHADES[props.colorScheme]) return base;
  return {
    ...base,
    container: solidBadgeVars(props.colorScheme, base.container ?? {}),
  };
};

const outlineField = (props: StyleFunctionProps) => {
  const base = chakraBaseTheme.components.Input.variants!.outline(props);
  return {
    ...base,
    field: {
      ...base.field,
      borderColor: "border.strong",
      _hover: { borderColor: "text.secondary" },
      _focusVisible: {
        zIndex: 1,
        outline: "2px solid",
        outlineColor: "brand.accent",
        outlineOffset: "2px",
        borderColor: "brand.accent",
        boxShadow: "none",
      },
    },
  };
};

const components = {
  Button: { variants: { solid: solidButton } },
  Badge: { variants: { solid: solidBadge } },
  Tag: { variants: { solid: solidTag } },
  Input: { variants: { outline: outlineField } },
  Select: { variants: { outline: outlineField } },
  // Chakra's FormErrorMessage is red.500 (3.94:1 on the light page). Form
  // errors sit on the bare page (login), so they use the page-safe error
  // step: AA on every ground incl. the worst Expressive page point (gate).
  FormError: { baseStyle: { text: { color: "status.errorStrong" } } },
};

const customTheme = extendTheme({
  config,
  colors,
  semanticTokens,
  radii,
  shadows,
  fonts,
  components,
  styles: {
    global: {
      "html, body": {
        backgroundColor: "bg.page",
        color: "text.primary",
      },
    },
  },
});

export default customTheme;
